import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Download, ShieldAlert, Check } from "lucide-react";

export function RecoveryCodesView({ codes, onDone }) {
  const [saved, setSaved] = useState(false);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("Recovery codes copied");
    } catch {
      toast.error("Clipboard unavailable — please copy manually");
    }
  };

  const download = () => {
    const header = "NavScora Admin — Two-Factor Recovery Codes\nKeep these safe. Each code works only once.\n\n";
    const blob = new Blob([header + codes.join("\n") + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "navscora-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="recovery-codes-view">
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-amber-200">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-xs leading-relaxed">
          Save these codes now. Use one if your authenticator device is unavailable — each works
          <span className="font-semibold"> only once</span>. NavScora
          <span className="font-semibold"> cannot show them again</span>.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-lg border border-[#2D3F62] bg-[#0B1120] p-3" data-testid="recovery-codes-list">
        {codes.map((c) => (
          <code key={c} className="select-all rounded bg-[#111A2E] px-2 py-1.5 text-center font-mono text-sm tracking-wider text-cyan-300">
            {c}
          </code>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1 border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" onClick={copyAll} data-testid="recovery-copy-all">
          <Copy className="mr-1.5 h-4 w-4" /> Copy all
        </Button>
        <Button variant="outline" className="flex-1 border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" onClick={download} data-testid="recovery-download">
          <Download className="mr-1.5 h-4 w-4" /> Download
        </Button>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)}
          className="h-4 w-4 accent-cyan-500" data-testid="recovery-saved-checkbox" />
        I have securely saved my recovery codes
      </label>

      <Button className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900 disabled:opacity-50"
        disabled={!saved} onClick={onDone} data-testid="recovery-done">
        <Check className="mr-1.5 h-4 w-4" /> Done
      </Button>
    </div>
  );
}
